import { ENTER } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit, ChangeDetectorRef, Component, ContentChild, EventEmitter, Input, OnInit, Output,
  QueryList, TemplateRef, ViewChild, ViewChildren
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client';
import { Query, QueryRef } from 'apollo-angular';
import { toSafeInteger } from 'lodash-es';
import { takeUntil } from 'rxjs/operators';

import { SearchSavedQueriesQuery } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ListItemComponent } from 'shared/components/list-item/list-item.component';
import {
  InfiniteSearchScrollableComponentTemplate, InfiniteSearchScrollAdaptor
} from 'shared/InfiniteSearchScrollAdaptor';

import { NoResultsTmplDirective } from './no-results-tmpl.directive';
import { ResultTmplDirective } from './results-temp.directive';

type ResultsNodeType = any;

export interface InfiniteScrollFetchMoreVariablesPayload {
  page: number; keyword?: string; itemsPerPage: number;
}

/**
 * Adapter to provide infinite scrolling of graphql results
 *
 * ```
 *  <app-graphql-infinite-scroll-search [getFetchMoreVariablesFn]="getFetchMoreVariablesFn"
 *                                         [selectQueryResultsFn]="selectQueryResultsFn"
 *                                         [graphqlQueryService]="searchSavedQueriesGQL"
 *                                         [searchPlaceholder]="'Search filter groups...'"
 *                                         [itemSize]="62"
 *                                         (itemSelected)="filterGroupSelected($event)"
 *                                         [itemsPerPage]="5">
 *
 *       <ng-container *resultTmpl="let item = item">
 *         <span class="d-block txt-13 color--gray mt-0025">Saved by {{item.user.name}}</span>
 *       </ng-container>
 *       <ng-container *noResultsTmpl>
 *         <div class="p-0075 pl-0100 color--gray">
 *           No Results
 *         </div>
 *       </ng-container>
 *
 *   </app-graphql-infinite-scroll-search>
 *  ```
 */
@Component({
  selector: 'app-graphql-infinite-scroll-search',
  templateUrl: './graphql-infinite-scroll-search.component.html',
  styleUrls: [`./grapqhl-infinite-scroll-search.component.scss`]
})
export class GraphQLInfiniteScrollSearchComponent extends WithDisposableSubject(
) implements
  OnInit, AfterViewInit, InfiniteSearchScrollableComponentTemplate<any> {

  /** Size of each scroll item */
  @Input() itemSize: any | number = 50;
  /** Items to fetch at one time */
  @Input() itemsPerPage = 25;

  /** Returns the variables to be passed the apollo query to fetch more data */
  @Input() getFetchMoreVariablesFn: (data: InfiniteScrollFetchMoreVariablesPayload) => any;

  /** Selects the results items from the apollo query result */
  @Input() selectQueryResultsFn: (data: ApolloQueryResult<any>) => any[];

  /** GraphQL Service to use for fetching results */
  @Input() graphqlQueryService: Query<any, any>;

  @Input() searchPlaceholder: string;

  /** Emits when item is selected */

  @Output() itemSelected = new EventEmitter<any>();

  /**
   * Calculated on init, determines the max number of items that can fit
   * within the scroller based upon the item size to adjust the size height of scrooler.
   */
  maxItemsInScroller: number;

  // <InfiniteSearchScrollableComponentTemplate Implementation>

  filterInput = new UntypedFormControl();

  resultsQuery: QueryRef<any, any>;

  // The retrieved results
  results = [];

  loading = false;

  // Initially true, since data is loading
  initLoading = true;

  private _infiniteSearchScrollAdaptor: InfiniteSearchScrollAdaptor<ResultsNodeType>;

  @ViewChild('scrollContainer', { static: true })
  private _scrollContainer: CdkVirtualScrollViewport;

  @ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;
  // </InfiniteSearchScrollableComponentTemplate>

  // Read in our structural directives as TemplateRefs
  @ContentChild(ResultTmplDirective, { read: TemplateRef }) resultTemplate;

  // Read in our structural directives as TemplateRefs
  @ContentChild(NoResultsTmplDirective, { read: TemplateRef }) noResultsTemplate;

  constructor(
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.maxItemsInScroller = Math.floor(250 / toSafeInteger(this.itemSize || 62));
    this.resultsQuery = this.graphqlQueryService.watch(this.getFetchMoreVariables(0));
    this._infiniteSearchScrollAdaptor = new InfiniteSearchScrollAdaptor<ResultsNodeType>(this);

    this._infiniteSearchScrollAdaptor.results.pipe(
      takeUntil(this.disposer)
    ).subscribe(r => {
      this.results = r;
      this.initLoading = false;
      this._cd.markForCheck();
    });
  }

  ngAfterViewInit() {
    this._infiniteSearchScrollAdaptor.connect();
  }

  getChangeDetectorRef() {
    return this._cd;
  }

  getScrollContainer(): CdkVirtualScrollViewport {
    return this._scrollContainer;
  }

  getFetchMoreVariables(page: number): any {
    return this.getFetchMoreVariablesFn({
      page,
      itemsPerPage: this.itemsPerPage,
      keyword: this.filterInput?.value
    });
  }

  selectResults(queryResult: ApolloQueryResult<SearchSavedQueriesQuery>) {
    return this.selectQueryResultsFn(queryResult);
  }

  onResultsListKeydown(event) {
    if (event.keyCode === ENTER) {
      const item = this._infiniteSearchScrollAdaptor.keyManager.activeItem?.item;
      if (item) {
        this.itemClicked(item);
      }
    } else {
      this._infiniteSearchScrollAdaptor.keyManager.onKeydown(event);
    }
  }

  itemClicked(item: ResultsNodeType) {
    this.itemSelected.emit(item);
  }

  focusResults() {
    this._infiniteSearchScrollAdaptor.keyManager.setFirstItemActive();
  }

  trackByIdFn(_index, item) {
    return item?.id + 'a';
  }

  onSearchArrowDown() {
    const keyManager = this._infiniteSearchScrollAdaptor?.keyManager;
    keyManager?.setFirstItemActive();
  }
}
